/**
 * The POS session: the layer between the screen and the order aggregate.
 *
 * Specification: ../test/session.spec.ts. Master plan §4 M4, §4 M1, §3 K4.
 *
 * It exists to make four rules structural rather than aspirational:
 *
 *   - EVERY ACTION IS AN EVENT on the durable local log. There is no method
 *     here that changes what a guest owes without emitting one, so the fraud
 *     journal and the fired-but-not-billed report cannot be bypassed by a UI
 *     shortcut. archive/v0 let a client PUT a whole check object, which
 *     destroys both and cannot be retrofitted.
 *   - THE UI NEVER BLOCKS ON THE NETWORK. Actions await the local store and
 *     nothing else.
 *   - NO RAW `Error` REACHES A USER. Failures carry a code and a message key,
 *     never a sentence — translatable into cs/uk/sk, and no stack trace on a
 *     screen a guest can read over the server's shoulder.
 *   - THE MENU IS EDGE-RESIDENT AND VERSIONED, so ringing during an outage is
 *     ordinary rather than degraded, and an open check cannot reprice under
 *     the person holding it.
 */

import {
  checkTotals,
  foldOrder,
  foldOrderConverging,
  isCheckVacant,
  settlementBalance,
  type CheckBuyer,
  type CheckRefund,
  type CheckTaxLine,
  type ConsumptionMode,
  type FulfillmentState,
  type OrderEvent,
  type OrderState,
  type PricedLine,
  type TenderKind,
  type AllowanceKind,
} from "@gf/module-orders";
import { netFromGross, scaleLine, vatFromGross } from "@gf/kernel/money";
import type { OutboundEvent } from "@gf/sync-client";
import { tryPlanTender, type TenderPlan } from "./tender";
import type { AllergenCode, LineModifier } from "@gf/contracts/values";
import {
  resolveChoices,
  type CategorySnapshot,
  type MenuChoice,
  type ModifierGroupSnapshot,
} from "@gf/module-catalog/choices";

/**
 * Where a selection goes when its item is not in the menu the till holds.
 *
 * It must still fire. Food that reaches no station is food nobody cooks, and a
 * refusal here would strand a guest's order because the back office renamed
 * something. A named bucket is visible on the board; silence is not.
 */
/** The course that goes with "Odeslat"; the later ones wait to be called. */
export const FIRST_COURSE = "1";

const UNROUTED_STATION = "station:unrouted";

/**
 * How many ways one check may be split.
 *
 * A BOUND RATHER THAN A POLICY, and a generous one. The operational advice in
 * the field is that a venue should set its own splits limit for large parties
 * and communicate it — eight separate checks for a birthday of eight at seven
 * on a Saturday is a legitimate thing to decline. That is a conversation
 * between a venue and its guests, not something a till should decide, so this
 * exists only to stop a mis-tap turning into two hundred checks.
 */
const MAX_SPLIT_WAYS = 24;

/**
 * Equal weights that sum to exactly 10 000 basis points.
 *
 * 10000/3 is not an integer, and the aggregate REJECTS an allocation set that
 * does not sum to the whole — deliberately, because normalising would quietly
 * move money between guests. So the remainder is handed out one basis point at
 * a time.
 *
 * The bias this leaves is below a haléř on any plausible line: one basis point
 * of a 189 CZK dish is two hundredths of one. What decides the actual money is
 * `allocateProportionally`'s largest-remainder pass in the aggregate, which
 * makes the shares sum to the gross exactly — three guests splitting 189 CZK
 * neither create nor destroy a haléř between them.
 */
function evenWeights(
  checkIds: readonly string[],
  /** Rotates which checks carry the leftover. See below. */
  offset = 0,
): { checkId: string; weightBp: number }[] {
  const n = checkIds.length;
  const base = Math.floor(10_000 / n);
  const remainder = 10_000 - base * n;

  /**
   * THE LEFTOVER MOVES, and this was a finding rather than a design.
   *
   * 10 000/3 is 3 333 with 1 left over. Handing the spare basis point to the
   * first check every time gives that guest 3 334 bp of EVERY line, so on a
   * two-line check they were three haléř worse off than the other two — a
   * systematic bias, small, and entirely avoidable. The spec caught it.
   *
   * The published guidance on splitting bills says the method must attribute
   * who covers the leftover and why, with the shares summing to the receipt
   * exactly. Rotating by selection does both: the sum is exact by
   * construction, and over a check of several lines the leftover lands on a
   * different guest each time rather than always the same one.
   */
  return checkIds.map((checkId, i) => ({
    checkId,
    weightBp: base + ((i - offset + n * n) % n < remainder ? 1 : 0),
  }));
}

/** One check of an order, as the strip along the top of the screen shows it. */
export interface CheckSummary {
  readonly checkId: string;
  readonly dueMinor: bigint;
  readonly outstandingMinor: bigint;
  readonly closed: boolean;
  /** Money has been taken against it, so its lines are frozen. */
  readonly paid: boolean;
  readonly current: boolean;
  /**
   * Nothing on it and still open — what "Sloučit zpět" leaves behind. It does
   * not hold the order open (`isCheckVacant` in @gf/module-orders) and the
   * strip does not show it: a guest with nothing to pay is not a guest.
   */
  readonly vacant: boolean;
}

// --- The replicated menu ---------------------------------------------------

/**
 * A menu item as the device holds it: price AND resolved tax rate per
 * consumption mode, decided by the server against the regulatory registry and
 * shipped down.
 *
 * This is what makes offline ringing an ordinary operation. Resolving VAT
 * needs the rule registry, so a POS that resolved at ring time could not sell
 * during an outage — which is precisely when it must.
 */
export interface MenuItemSnapshot {
  readonly itemRef: string;
  readonly name: string;
  readonly stationId: string;
  /**
   * Annex II codes the dish CONTAINS or MAY_CONTAIN.
   *
   * Carried from the frozen menu version so the till can put them on the
   * ticket, which is what the acknowledgement gate on the kitchen screen reads.
   * An empty list is a declaration; it is not the same as not knowing, and the
   * four-valued rollup that decides the difference lives in the back office.
   */
  readonly allergens: readonly AllergenCode[];
  /**
   * Whether selling this needs somebody to have looked at a face.
   *
   * Carried from the frozen menu version alongside the allergens, and copied
   * onto the selection when the item is rung — so the fire gate reads what the
   * menu said WHEN IT WAS ORDERED rather than what it says now.
   */
  readonly ageRestricted: boolean;
  readonly byMode: Readonly<Partial<Record<ConsumptionMode, PricedLine>>>;
  readonly categoryId: string | null;
  readonly description: string | null;
  /** What the server must or may choose when ringing it, in the order offered. */
  readonly modifierGroups: readonly ModifierGroupSnapshot[];
}

export interface MenuSnapshot {
  readonly version: number;
  readonly resolvedAt: Date;
  readonly categories: readonly CategorySnapshot[];
  readonly items: readonly MenuItemSnapshot[];
}

// --- Results ---------------------------------------------------------------

export type PosErrorCode =
  | "NO_ORDER"
  | "ORDER_SETTLED"
  | "UNKNOWN_ITEM"
  | "UNKNOWN_LINE"
  | "REASON_REQUIRED"
  /**
   * A buyer with no name. The one field a tax document cannot do without — a
   * blank where the buyer belongs is not a document, and the till is the last
   * place that can say so to the person holding the keyboard.
   */
  | "BUYER_NAME_REQUIRED"
  /**
   * The check is settled, so its document is printed and in somebody's hand.
   * Naming a buyer now produces a filed record that disagrees with the paper,
   * and the paper is the one the tax authority will be shown.
   */
  | "CHECK_SETTLED"
  | "NOT_BALANCED"
  | "LINE_SENT"
  | "TENDER_REFUSED"
  | "UNKNOWN_TENDER"
  /**
   * No receipt number left to issue, so the check cannot CLOSE.
   *
   * The only failure code here that does not mean somebody mis-tapped. It stops
   * closing and nothing else: ringing and firing continue, because a seated
   * guest still has to be served and a kitchen ticket is not a receipt.
   */
  | "NO_RECEIPT_NUMBER"
  /**
   * A line on this order is age-restricted and nobody has said they checked.
   *
   * NOT A MIS-TAP either, and not an error in the ordinary sense: it is the
   * control working. The screen turns it into a prompt naming the lines, and a
   * confirmation records WHO — because what a venue needs when asked is a name
   * and a timestamp, not "our system prompts staff".
   */
  | "AGE_CHECK_REQUIRED"
  /**
   * The kitchen has run out, and somebody tapped it anyway.
   *
   * Not a mis-tap and not an error: it is 86'ing working. The grid greys the
   * tile, and this is what makes that true rather than decorative — the same
   * reason the age gate lives in `fire()` and not in the dialog.
   */
  | "UNAVAILABLE"
  /**
   * A discount or comp that is not a number this line can carry.
   *
   * Refused on the device rather than left to the aggregate, so the person who
   * typed it is told rather than the event sitting rejected in an outbox.
   */
  | "ALLOWANCE_INVALID"
  /** No such check on this order — a stale screen, or a bad id. */
  | "UNKNOWN_CHECK"
  /**
   * The check has been paid or closed, so its lines may not move.
   *
   * The best-documented failure in the field, guarded by default here: a guest
   * who has paid and left must not acquire somebody else's steak, and a guest
   * who has paid must not have a line pulled off theirs either.
   */
  | "CHECK_FROZEN"
  /** The split itself does not make sense — too many ways, or already split. */
  | "SPLIT_REFUSED"
  /** A required choice missing, or one the item does not offer. */
  | "CHOICE_INVALID"
  /** Only a closed check is taken back; an open one has its lines voided. */
  | "CHECK_NOT_CLOSED"
  | "ALREADY_REFUNDED";

/**
 * A failure the UI can render.
 *
 * `messageKey` rather than a message: the copy lives in the translation
 * catalogue, so it can be Czech for the manager and Ukrainian for the cook,
 * and so no exception text can ever reach a screen.
 */
export interface PosFailure {
  readonly ok: false;
  readonly code: PosErrorCode;
  readonly messageKey: string;
  readonly params?: Readonly<Record<string, string>>;
}

export type PosResult<T> = { readonly ok: true; readonly value: T } | PosFailure;

const fail = (
  code: PosErrorCode,
  messageKey: string,
  params?: Readonly<Record<string, string>>,
): PosFailure => (params ? { ok: false, code, messageKey, params } : { ok: false, code, messageKey });

// --- The view model --------------------------------------------------------

/**
 * A line as the screen shows it.
 *
 * The affordances are computed HERE rather than in the component, so a screen
 * cannot drift from the domain rules. Offering a quantity control on a fired
 * item teaches staff the system is broken when the change is refused.
 */
export interface LineView {
  readonly selectionId: string;
  readonly itemRef: string;
  readonly name: string;
  readonly quantityMilli: number;
  /** The WHOLE line, whatever it is split across. */
  readonly grossMinor: bigint;
  /**
   * What THIS check owes for the line, which is not the same number once an
   * order is split.
   *
   * The screen showed `grossMinor` on every check, so a guest who had split a
   * bill three ways read "Guláš 189,00" on a check that came to 82,67 and
   * reasonably concluded the till was broken. Lightspeed solves the same
   * problem by showing a fractional quantity — "0.25 beers" — and either is
   * better than a number that does not belong to the bill it is printed on.
   *
   * Equal to `grossMinor` on an unsplit check, which is the ordinary case and
   * why this went unnoticed until splitting existed.
   */
  readonly shareMinor: bigint;
  /** Basis points of the line on this check. 10 000 when it is not shared. */
  readonly shareBp: number;
  readonly rateBp: number;
  readonly stationId: string;
  readonly fulfillmentState: FulfillmentState;
  readonly voided: boolean;
  readonly canEditQuantity: boolean;
  readonly canVoid: boolean;
  /** Why editing is refused, as a message key. Null when it is not. */
  readonly lockedReasonKey: string | null;
  /** The course this line goes out with ("1", "2"…); null at a table without courses. */
  readonly courseId: string | null;
  readonly modifiers: readonly LineModifier[];
  readonly note: string | null;
}

export interface TotalsView {
  readonly grossMinor: bigint;
  readonly discountMinor: bigint;
  readonly compMinor: bigint;
  readonly dueMinor: bigint;
  readonly vatMinor: bigint;
  /** Non-taxable, and shown: a guest who is charged 235,00 on a 234,50 bill
   *  is entitled to see the 50 haléře named rather than absorbed. */
  readonly roundingMinor: bigint;
  readonly tipMinor: bigint;
  readonly outstandingMinor: bigint;
  readonly taxLines: readonly CheckTaxLine[];
}

export interface CheckView {
  readonly orderId: string | null;
  readonly checkId: string | null;
  readonly tableRef: string | null;
  readonly consumptionMode: ConsumptionMode;
  readonly state: "EMPTY" | "OPEN" | "SETTLED" | "ABANDONED";
  readonly lines: readonly LineView[];
  readonly totals: TotalsView;
  /** Which menu version priced this check. Printed on the audit trail. */
  readonly menuVersion: number;
  /** Enables or disables Send without the component knowing the rules. */
  readonly unfiredCount: number;
  /** Courses rung and held back from the kitchen until called, in order. */
  readonly heldCourses?: readonly string[];
  readonly version: number;
  readonly tenders: readonly TenderView[];
  /**
   * Who the document is made out to, once somebody has asked. §29 ZDPH.
   *
   * Null on an ordinary check, which is the overwhelming majority — and the
   * distinction the screen needs: "nobody asked" and "a company with no name"
   * must not look alike, because the second renders a document with a blank
   * where the buyer belongs.
   */
  readonly buyer: CheckBuyer | null;
  /** Taken back after it closed, under its own document number. */
  readonly refund?: CheckRefund | null;
  /**
   * THIS check has closed. Differs from `state === "SETTLED"` on a split table,
   * where one guest's check closes while the order is still trading — and that
   * guest's receipt, and a screen that stops offering Zaplatit, depend on it.
   * Absent only on views built by hand before the field existed.
   */
  readonly closed?: boolean;
  /** When the table was opened, and by whom — for the floor. Absent on an empty view. */
  readonly openedAt?: Date | null;
  readonly openedBy?: string | null;
}

// --- Session ---------------------------------------------------------------

export interface PosSessionDeps {
  /**
   * Anything that accepts an event, not the concrete `Outbox`.
   *
   * The session calls exactly one method on it. Typing the dependency as the
   * whole class meant nothing could sit in the emit path — and the drawer has
   * to, because deriving cash takings from the same tender events the server
   * folds is the only way the two can agree.
   */
  readonly outbox: { enqueue(event: OutboundEvent): Promise<void> };
  readonly menu: MenuSnapshot;
  readonly actorId: string;
  readonly deviceId: string;
  readonly now: () => Date;
  readonly nextHlc: () => string;
  readonly newId: () => string;
  /**
   * What is 86'd, right now — a GETTER, not a snapshot.
   *
   * A function because availability changes during service and sessions are
   * long-lived: a check opened at seven must refuse a dish the kitchen ran out
   * of at eight, and re-creating every open session to tell it so would throw
   * away the floor.
   *
   * Optional because a session that never rings does not need one, and most
   * specs construct one to exercise firing or tendering. Absent means nothing
   * is off, which is the same thing a till with no answer from the server yet
   * believes — and the direction that keeps a venue selling rather than
   * refusing everything because a fetch failed.
   */
  readonly unavailable?: () => ReadonlySet<string>;
  /**
   * Where a receipt number comes from at close.
   *
   * OPTIONAL, and that is a transitional convenience rather than a statement
   * that a receipt number is optional: several specs construct a session to
   * exercise ringing and firing, neither of which needs a number. A session
   * without one simply cannot close a check, which is the honest behaviour for
   * a till that has no numbers.
   */
  readonly receiptNumbers?: {
    issue(checkId: string): Promise<{
      leaseId: string;
      seriesKey: string;
      period: string;
      number: number;
      receiptNumber: string;
    } | null>;
  };
}

export interface OpenOrderArgs {
  readonly tableRef: string | null;
  readonly consumptionMode: ConsumptionMode;
  /** A sale at the counter: paid first, collected by a number. */
  readonly orderType?: "COUNTER";
  /** The number the guest is called by ("A07"); a counter order's `tableRef` is `pickup:A07`. */
  readonly pickupCode?: string;
  /** Guests at the table, when known — from the booking they were seated from. */
  readonly covers?: number;
  /** The booking the table is seated from; the server marks it seated from this. */
  readonly reservationRef?: string;
}

export interface VoidArgs {
  readonly reasonCode: string;
  readonly authorizedBy: string;
}

/**
 * What the screen knows: a tender kind and, for cash, the note that was handed
 * over. The AMOUNT and the ROUNDING are computed here rather than supplied —
 * a caller that could pass its own rounding could put any number it liked into
 * a non-taxable line that no VAT report inspects.
 */
export interface TenderArgs {
  readonly kind: TenderKind;
  /** Handed over or keyed. Omit to settle the outstanding balance exactly. */
  readonly offeredMinor?: bigint;
  readonly tipMinor?: bigint;
  /** From the regulatory registry (K11). Defaults to CZ whole koruna. */
  readonly cashIncrementMinor?: bigint;
}

export interface TenderVoidArgs {
  readonly reasonCode: string;
  readonly authorizedBy: string;
}

/** A recorded tender as the screen lists it, so a mis-tap can be reversed. */
export interface TenderView {
  readonly tenderId: string;
  readonly kind: TenderKind;
  readonly amountMinor: bigint;
  readonly roundingMinor: bigint;
  readonly tipMinor: bigint;
  readonly voided: boolean;
}

const EMPTY_TOTALS: TotalsView = {
  grossMinor: 0n,
  discountMinor: 0n,
  compMinor: 0n,
  dueMinor: 0n,
  vatMinor: 0n,
  roundingMinor: 0n,
  tipMinor: 0n,
  outstandingMinor: 0n,
  taxLines: [],
};

export class PosSession {
  readonly #deps: PosSessionDeps;
  /**
   * NOT `readonly` any more — `sync` replaces the array wholesale.
   *
   * It was, correctly, while the till was the only writer and history only ever
   * grew at the end. Merging the server's copy means rebuilding the sequence,
   * and a push-only array cannot express that.
   */
  #events: OrderEvent[] = [];

  #menu: MenuSnapshot;
  #pendingMenu: MenuSnapshot | null = null;
  #state: OrderState | undefined;
  #checkId: string | null = null;

  constructor(deps: PosSessionDeps) {
    this.#deps = deps;
    this.#menu = deps.menu;
  }

  get menuVersion(): number {
    return this.#menu.version;
  }

  /** The version waiting for the check to settle, or null. */
  get pendingMenuVersion(): number | null {
    return this.#pendingMenu?.version ?? null;
  }

  /**
   * Take delivery of a newer menu.
   *
   * Staged rather than applied while a check is open. A server rings a lunch
   * item at 11:58, the daypart flips at 12:00 and a new version publishes —
   * the line already on the check must not reprice, and the item must not
   * vanish out from under the person holding the handheld.
   */
  offerMenu(menu: MenuSnapshot): void {
    if (this.#state && this.#state.status === "OPEN") {
      this.#pendingMenu = menu;
      return;
    }
    this.#menu = menu;
    this.#pendingMenu = null;
  }

  /**
   * Append an event locally and queue it for upload.
   *
   * Awaits the LOCAL store only. Nothing here touches the network, which is
   * what makes ringing an item during an outage indistinguishable from ringing
   * one online.
   */
  async #emit<T extends OrderEvent["type"]>(
    type: T,
    payload: Extract<OrderEvent, { type: T }>["payload"],
  ): Promise<void> {
    return this.#append(type, payload as Record<string, unknown>);
  }

  /**
   * The same path for an event this module does not own.
   *
   * `ticket.fired` belongs to the kitchen and rides the ORDER stream, because
   * one order fired to two stations is two tickets and one order. It must go
   * through here — the local fold, the stream id, the outbox — and `#emit`'s
   * type parameter is deliberately the order union, so a typo in an ORDER event
   * name is still a compile error rather than a silent no-op.
   *
   * `foldOrder` ignores what it does not own, which is what makes a shared
   * stream work at all.
   */
  async #fireTicket(payload: Record<string, unknown>): Promise<void> {
    return this.#append("ticket.fired", payload);
  }

  /**
   * Take the server's copy of this order and merge it with ours.
   *
   * THE TILL IS NO LONGER THE ONLY WRITER, and this is what makes that safe.
   *
   * It used to be: a session was opened here, every event was appended here,
   * and the outbox pushed them up. Nothing ever came back, because nothing
   * needed to. Then a guest's phone became able to put a line on a check, and
   * that assumption quietly became a way to under-bill a table — the server
   * fires the food, the guest eats it, and a till tendering from its own local
   * history has never heard of it.
   *
   * MERGED BY EVENT ID, NOT REPLACED. Anything of ours the server has not
   * accepted yet is still in the outbox and still ours to keep: replacing the
   * history with the server's would discard a round rung during an outage,
   * which is the exact case the offline story exists for.
   *
   * `foldOrderConverging` rather than `foldOrder`, because the merged sequence
   * genuinely can be out of order — our unsent events are newer than the
   * server's but were created before some of them.
   */
  async sync(serverEvents: readonly OrderEvent[]): Promise<PosResult<CheckView>> {
    const seen = new Set(serverEvents.map((e) => e.eventId));
    const mine = this.#events.filter((e) => !seen.has(e.eventId));

    this.#events = [...serverEvents, ...mine];
    this.#state = foldOrderConverging(this.#events);

    /**
     * THE CHECK IDENTITY IS ADOPTED TOO, not just the state.
     *
     * `#checkId` is set by `openOrder` and nothing else, and `view()` returns
     * an EMPTY check without it — so a session that synced a real order with
     * real lines on it rendered a blank screen and reported no totals. Caught
     * by an end-to-end test, because every unit of this was individually right.
     *
     * The order's PRIMARY check, which is the one a till serves. Splitting adds
     * more, and choosing between them is a decision the server makes on the
     * screen rather than one a sync should make silently.
     */
    if (this.#state) this.#checkId = this.#state.primaryCheckId;

    return { ok: true, value: this.view() };
  }

  async #append(type: string, payload: Record<string, unknown>): Promise<void> {
    const event = {
      eventId: this.#deps.newId(),
      type,
      payload,
      occurredAt: this.#deps.now(),
      hlc: this.#deps.nextHlc(),
      actorId: this.#deps.actorId,
    } as OrderEvent;

    this.#events.push(event);
    this.#state = foldOrder(this.#events);
    await this.#deps.outbox.enqueue({
      eventId: event.eventId,
      // The ORDER is the stream. Taken from the folded state, so every event of
      // one order shares it — including order.opened, which establishes it.
      streamId: this.#state?.orderId ?? (payload as { orderId?: string }).orderId ?? event.eventId,
      type: event.type,
      payload: event.payload,
      hlc: event.hlc,
      occurredAt: event.occurredAt,
      // Read at EMIT time, so a session that outlives a sign-in attributes each
      // event to whoever was actually holding the handheld.
      actorId: this.#deps.actorId,
    });
  }

  #guard(): PosFailure | null {
    if (!this.#state) return fail("NO_ORDER", "pos.error.noOrder");
    if (this.#state.status !== "OPEN") return fail("ORDER_SETTLED", "pos.error.orderSettled");
    return null;
  }

  async openOrder(args: OpenOrderArgs): Promise<PosResult<CheckView>> {
    const checkId = this.#deps.newId();
    this.#checkId = checkId;
    await this.#emit("order.opened", {
      orderId: this.#deps.newId(),
      orderType: args.orderType ?? (args.consumptionMode === "DINE_IN" ? "DINE_IN" : "TAKEAWAY"),
      consumptionMode: args.consumptionMode,
      checkId,
      ...(args.tableRef !== null ? { tableRef: args.tableRef } : {}),
      ...(args.pickupCode ? { pickupCode: args.pickupCode } : {}),
      ...(args.covers ? { covers: args.covers } : {}),
      ...(args.reservationRef ? { reservationRef: args.reservationRef } : {}),
    });
    return { ok: true, value: this.view() };
  }

  async ring(
    itemRef: string,
    options: {
      readonly quantityMilli?: number;
      readonly choices?: readonly MenuChoice[];
      readonly note?: string;
      /** The course it goes out with; none at a table that does not use courses. */
      readonly courseId?: string;
    } = {},
  ): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const item = this.#menu.items.find((i) => i.itemRef === itemRef);
    if (!item) return fail("UNKNOWN_ITEM", "pos.error.unknownItem", { itemRef });

    const priced = item.byMode[this.#state!.consumptionMode];
    if (!priced) {
      return fail("UNKNOWN_ITEM", "pos.error.notAvailableInMode", {
        itemRef,
        mode: this.#state!.consumptionMode,
      });
    }

    /**
     * 86'D — REFUSED HERE, not only greyed out on the grid.
     *
     * The grid disables the tile, which is what a server sees. This is what
     * makes it true: a gate that lives in a component is a gate the next caller
     * does not have, and this session is reachable from a floor restore, from a
     * handheld, and from the history generator. The same reasoning as the age
     * check, which is enforced in `fire()` rather than in the dialog.
     *
     * The item stays in the menu snapshot and simply cannot be rung, which is
     * the difference between "sold out" and "we never sold this" — and is why
     * 86'ing does not republish a menu version.
     */
    if (this.#deps.unavailable?.().has(itemRef) === true) {
      return fail("UNAVAILABLE", "pos.error.soldOut", { name: item.name });
    }

    // Refused here rather than fixed up: a required "Příloha" left empty is a
    // plate the kitchen cannot finish, and it is caught before it is rung.
    const chosen = resolveChoices(item.modifierGroups, options.choices ?? []);
    if (!chosen.ok) {
      return fail("CHOICE_INVALID", "pos.error.choiceInvalid", {
        name: item.name,
        group: item.modifierGroups.find((g) => g.groupId === chosen.violation.groupId)?.name ?? "",
      });
    }
    const unitGross = priced.grossMinor + chosen.unitDeltaMinor;
    if (unitGross < 0n) {
      return fail("CHOICE_INVALID", "pos.error.choiceInvalid", { name: item.name, group: "" });
    }
    // The choices move the gross; the rate is the item's, so the VAT is
    // recomputed from the new gross exactly as the menu computed it.
    const unitVat = vatFromGross(unitGross, priced.rateBp);
    const unit: PricedLine = { ...priced, grossMinor: unitGross, vatMinor: unitVat, netMinor: unitGross - unitVat };
    const note = options.note?.trim();

    /**
     * THE SAME BEER AGAIN IS ONE MORE ON ITS LINE, not a new line.
     *
     * Only a plain line — no choices, no note, not sent, no discount, wholly on
     * this check — and only for a plain tap. Anything else is a different plate
     * and keeps its own line.
     */
    const adding = options.quantityMilli ?? 1000;
    if (chosen.modifiers.length === 0 && !note && adding % 1000 === 0) {
      const twin = [...this.#state!.selections.values()].find(
        (s) =>
          s.itemRef === item.itemRef &&
          s.quantityMilli % 1000 === 0 &&
          s.voided === null &&
          s.fulfillmentState === "NEW" &&
          s.modifiers.length === 0 &&
          s.note === null &&
          // The same dish in another course is another plate, going out at another time.
          (s.courseId ?? null) === (options.courseId ?? null) &&
          s.priced.rateBp === priced.rateBp &&
          s.allocations.length === 1 &&
          s.allocations[0]!.checkId === this.#checkId &&
          ![...this.#state!.allowances.values()].some((a) => a.selectionId === s.selectionId),
      );
      if (twin) return this.changeQuantity(twin.selectionId, twin.quantityMilli + adding);
    }

    const quantityMilli = options.quantityMilli ?? 1000;
    await this.#emit("selection.added", {
      selectionId: this.#deps.newId(),
      itemRef: item.itemRef,
      name: item.name,
      quantityMilli,
      priced: scaleLine(unit, quantityMilli),
      ...(chosen.modifiers.length > 0 ? { modifiers: chosen.modifiers } : {}),
      ...(note ? { note } : {}),
      ...(options.courseId ? { courseId: options.courseId } : {}),
      // FROZEN NOW, like the price. The kitchen ticket needs them at fire time,
      // and looking them up then is a lookup that can fail — after a menu
      // change, after a reload — with an empty list the only thing left to
      // send, which is a positive claim that nothing is declared.
      allergens: [...item.allergens],
      /**
       * THE CURRENT CHECK, NAMED — and leaving it out was a live bug.
       *
       * `selection.added`'s `checkId` is optional and the reducer falls back to
       * the PRIMARY check, so on a split order every new line landed on the
       * first guest's bill however carefully the server had chosen a different
       * one. D8 names "split then add an item" as the #1 source of "the POS ate
       * my check", and it was in here.
       *
       * Invisible before splitting existed, because there was only ever one
       * check for the fallback to find.
       */
      ...(this.#checkId ? { checkId: this.#checkId } : {}),
      // Frozen with them, and for the same reason: looking this up at FIRE
      // time is a lookup that can fail after a menu change, and the direction
      // it fails in turns a gated line into an ungated one.
      ageRestricted: item.ageRestricted,
    });
    return { ok: true, value: this.view() };
  }

  /**
   * Send everything unfired to the kitchen, BY STATION.
   *
   * This used to emit one `selection.sent` with `stationRef: "station:all"` — a
   * placeholder no station is called. Everything a venue rang went to a station
   * that does not exist, so routing found nothing and the kitchen module, which
   * has had a complete ticket board since it was written, had nothing to draw.
   *
   * Each station also gets a `ticket.fired`, because a ticket is what a kitchen
   * works from: names, quantities and allergens, not selection ids it has no
   * way to resolve.
   *
   * THE ALLERGENS COME OFF THE SELECTION, frozen when the item was rung exactly
   * as the price was. Looking them up here would be a lookup that can fail —
   * after a menu change, after a reload — and the only thing to send when it
   * fails is an empty list, which is a positive claim that nothing is declared.
   */
  /**
   * Record that somebody checked an age, then the caller may fire.
   *
   * Separate from `fire` on purpose: they are two acts, potentially by two
   * people, minutes apart — a guest orders at 20:04, somebody walks over at
   * 20:06, the bar pours at 20:07. One call that did both would lose the only
   * fact an inspector is interested in.
   */
  /**
   * How many of an unsent line. The price moves with it; the unit price is the
   * line's own, so a line rung before a menu change keeps its price.
   */
  async changeQuantity(selectionId: string, quantityMilli: number): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;
    const selection = this.#state!.selections.get(selectionId);
    if (!selection || selection.voided) return fail("UNKNOWN_LINE", "pos.error.unknownLine");
    if (selection.fulfillmentState !== "NEW") return fail("LINE_SENT", "pos.error.lineSent");
    if (selection.allocations.some((a) => this.#frozen(a.checkId))) {
      return fail("CHECK_FROZEN", "pos.error.checkFrozen", { checkId: selection.allocations[0]!.checkId });
    }
    if ([...this.#state!.allowances.values()].some((a) => a.selectionId === selectionId)) {
      return fail("LINE_SENT", "pos.error.lineHasAllowance");
    }
    // Zero is a void, with a reason, not a quantity.
    if (!Number.isInteger(quantityMilli) || quantityMilli < 1000 || quantityMilli % 1000 !== 0) {
      return fail("UNKNOWN_LINE", "pos.error.badQuantity");
    }
    if (quantityMilli === selection.quantityMilli) return { ok: true, value: this.view() };

    // Whole units only on this path, so the unit price divides out exactly.
    const units = BigInt(selection.quantityMilli / 1000);
    const unitGross = selection.priced.grossMinor / units;
    const unitVat = vatFromGross(unitGross, selection.priced.rateBp);
    const unit: PricedLine = { ...selection.priced, grossMinor: unitGross, vatMinor: unitVat, netMinor: unitGross - unitVat };
    await this.#emit("selection.requantified", {
      selectionId,
      quantityMilli,
      priced: scaleLine(unit, quantityMilli),
    });
    return { ok: true, value: this.view() };
  }

  /** The party moved. The order, its checks and its history go with them. */
  async transferTable(tableRef: string): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;
    if (tableRef === this.#state!.tableRef) return { ok: true, value: this.view() };
    await this.#emit("order.table_changed", { tableRef });
    return { ok: true, value: this.view() };
  }

  /**
   * Let go of a table opened by mistake. Only one nothing happened on — the
   * aggregate refuses the rest, and so does this, before anything is queued.
   */
  async abandon(): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;
    const selections = [...this.#state!.selections.values()];
    if (selections.some((s) => s.voided === null || s.firedAt !== null) || this.#state!.tenders.size > 0) {
      return fail("ORDER_SETTLED", "pos.error.notEmpty");
    }
    await this.#emit("order.abandoned", { reasonCode: "OPENED_BY_MISTAKE" });
    return { ok: true, value: this.view() };
  }

  async verifyAge(selectionIds: readonly string[]): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    for (const selectionId of selectionIds) {
      /**
       * ALREADY-CHECKED LINES ARE SKIPPED, not re-emitted.
       *
       * The aggregate keeps the first name and ignores a second, so emitting
       * anyway would be harmless — and would put an event in an append-only log
       * that changes nothing and advances the version. Two servers both
       * confirming is ordinary; a log that records the second one as an act is
       * a log that makes an inspector ask which of them actually looked.
       */
      const selection = this.#state!.selections.get(selectionId);
      if (!selection || selection.ageVerifiedBy !== null) continue;

      await this.#emit("selection.age_verified", {
        selectionId,
        // WHO IS SIGNED IN, never a parameter. A verification the caller could
        // attribute to anybody is not a verification.
        verifiedBy: this.#deps.actorId,
      });
    }
    return { ok: true, value: this.view() };
  }

  /**
   * Send to the kitchen. Plainly: course 1 and anything with no course — later
   * courses are HELD until called, `fire({ courseId: "2" })`.
   */
  async fire(options: { readonly courseId?: string } = {}): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const unfired = [...this.#state!.selections.values()].filter(
      (s) =>
        !s.voided &&
        s.fulfillmentState === "NEW" &&
        (options.courseId !== undefined
          ? s.courseId === options.courseId
          : s.courseId === undefined || s.courseId === FIRST_COURSE),
    );
    if (unfired.length === 0) return { ok: true, value: this.view() };

    /**
     * NOTHING AGE-RESTRICTED LEAVES HERE WITHOUT A NAME ON IT.
     *
     * Refused rather than fired-with-a-warning, and refused HERE rather than in
     * the screen, because a gate that lives in a component is a gate the next
     * caller does not have. A guest ordering a beer from their phone reaches
     * this function without passing through any dialog at all — the server-side
     * path deliberately adds the line and does not send it, and this is what
     * makes that hold mean something when somebody finally presses Fire.
     *
     * The caller is told WHICH lines, so it can name them in the prompt. A
     * dialog that says "this order needs an age check" without saying what is
     * a dialog people learn to confirm without reading.
     */
    /**
     * RUNG BY A SERVER IS CHECKED BY THAT SERVER.
     *
     * Somebody at this till looked at the guest to take the order; that is the
     * check, and it is recorded in their name without a dialog on every beer.
     * A line a guest added from their phone was seen by nobody, and still waits
     * for a person to confirm.
     */
    for (const s of unfired) {
      if (s.ageRestricted && s.ageVerifiedBy === null && s.addedBy.startsWith("staff:")) {
        await this.#emit("selection.age_verified", { selectionId: s.selectionId, verifiedBy: s.addedBy });
      }
    }
    const firing = new Set(unfired.map((s) => s.selectionId));
    const unverified = [...this.#state!.selections.values()].filter(
      (s) => firing.has(s.selectionId) && s.fulfillmentState === "NEW" && s.ageRestricted && s.ageVerifiedBy === null,
    );
    if (unverified.length > 0) {
      return fail("AGE_CHECK_REQUIRED", "pos.age.required", {
        items: unverified.map((s) => s.name).join(", "),
        selectionIds: unverified.map((s) => s.selectionId).join(","),
      });
    }

    // Grouped by the station the ITEM belongs to, which the menu snapshot has
    // carried all along. A Map keeps insertion order, so the grill ticket comes
    // out in the order the server rang it.
    // One ticket per station AND course: the kitchen sees what it is cooking for when.
    const byStation = new Map<string, typeof unfired>();
    for (const selection of unfired) {
      const station =
        this.#menu.items.find((i) => i.itemRef === selection.itemRef)?.stationId ?? UNROUTED_STATION;
      const key = `${station}\u0000${selection.courseId ?? ""}`;
      const group = byStation.get(key);
      if (group) group.push(selection);
      else byStation.set(key, [selection]);
    }

    for (const [key, selections] of byStation) {
      const stationId = key.split("\u0000")[0]!;
      const courseId = selections[0]!.courseId;
      await this.#emit("selection.sent", {
        selectionIds: selections.map((s) => s.selectionId),
        stationRef: stationId,
      });
      await this.#fireTicket({
        ticketId: this.#deps.newId(),
        orderId: this.#state!.orderId,
        stationId,
        tableRef: this.#state!.tableRef ?? "",
        ...(this.#state!.pickupCode ? { pickupCode: this.#state!.pickupCode } : {}),
        ...(courseId ? { courseId } : {}),
        items: selections.map((s) => ({
          selectionId: s.selectionId,
          name: s.name,
          quantityMilli: s.quantityMilli,
          allergens: [...s.allergens],
          ...(s.modifiers.length > 0
            ? { modifiers: s.modifiers.map((m) => ({ name: m.name, quantity: m.quantity })) }
            : {}),
          ...(s.note !== null ? { note: s.note } : {}),
        })),
      });
    }

    return { ok: true, value: this.view() };
  }

  /**
   * Dine-in becomes takeaway, or the reverse — and the VAT follows.
   *
   * THE MOST COMMON CZECH TRANSACTION AFTER CASH, and until now impossible: the
   * till hardcoded `DINE_IN` when a check was opened and `CheckPanel` rendered a
   * takeaway label nothing could set. Both events this emits were declared,
   * validated and reduced, with no caller.
   *
   * §47 ZDPH: the same guláš is 12 % eaten in and 21 % taken away, and the rate
   * is a property of the SUPPLY rather than of the product. A venue that got
   * this wrong under-declares VAT on every takeaway order it ever took.
   *
   * THE GROSS DOES NOT MOVE. A guest told 229 Kč pays 229 Kč whichever way they
   * eat it; what changes is how much of that is tax. Recomputing the gross from
   * the new mode's menu price would silently re-price a check somebody has
   * already been quoted — and `selection.retaxed` deliberately carries no
   * gross, so the aggregate could not apply it anyway.
   *
   * ALLOWED ON A LINE THAT HAS ALREADY FIRED, which is why the reducer permits
   * it: the guest changed their mind at the counter, the food is cooked, and
   * the law that applies to it changed with the answer. Refusing would leave a
   * venue with a fired check it cannot tax correctly.
   *
   * A REASON IS REQUIRED. A mode change moves money between the VAT line and
   * the net line on a document that has to be explicable months later, and the
   * event carries the reason precisely so it can be.
   */
  async setConsumptionMode(
    mode: ConsumptionMode,
    args: { readonly reason: string },
  ): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const reason = args.reason.trim();
    if (reason.length === 0) {
      return fail("REASON_REQUIRED", "pos.error.reasonRequired");
    }

    if (this.#state!.consumptionMode === mode) return { ok: true, value: this.view() };

    await this.#emit("order.consumption_mode_changed", { consumptionMode: mode, reason });

    /**
     * THEN EVERY LINE, one event each.
     *
     * Separate from the mode change on purpose — the reducer's comment says it:
     * "only the mode moves here. Re-resolving VAT on the existing lines is a
     * separate decision made against the rule registry". A single event
     * carrying both would make the aggregate re-derive tax, which is the one
     * thing the whole snapshot-at-ring-time design exists to keep out of it.
     *
     * A VOIDED LINE IS SKIPPED. It was never sold, so there is nothing to tax
     * and retaxing it would put a correction on a line that is not on the bill.
     */
    for (const selection of this.#state!.selections.values()) {
      if (selection.voided) continue;

      const item = this.#menu.items.find((i) => i.itemRef === selection.itemRef);
      const target = item?.byMode[mode];
      /**
       * NO RATE FOR THIS MODE IS NOT AN ERROR AND NOT A GUESS.
       *
       * A dish a venue does not sell for takeaway keeps the tax it was rung
       * with. Inventing a rate here would be exactly the "tax is a column"
       * mistake; leaving it is visible on the receipt, where the line shows a
       * rate that disagrees with the check's mode and somebody can ask.
       */
      if (!target) continue;
      if (target.rateBp === selection.priced.rateBp) continue;

      /**
       * COMPUTED FROM THIS LINE'S OWN GROSS, not taken from the menu snapshot.
       *
       * The snapshot's takeaway entry carries its own gross, and a venue MAY
       * price takeaway differently — but this check has already been quoted at
       * the price it was rung at. Taking the new mode's net and VAT wholesale
       * would move the total under a guest who has been told a number.
       */
      const grossMinor = selection.priced.grossMinor;
      await this.#emit("selection.retaxed", {
        selectionId: selection.selectionId,
        rateBp: target.rateBp,
        vatMinor: vatFromGross(grossMinor, target.rateBp),
        netMinor: netFromGross(grossMinor, target.rateBp),
        // The CITATION for the new rate, so the receipt can be re-explained
        // after the rules have moved.
        taxSourceRef: target.taxSourceRef,
      });
    }

    return { ok: true, value: this.view() };
  }

  /**
   * Take money off a line — a DISCOUNT, or a COMP.
   *
   * D9 KEEPS THE TWO APART AND THIS IS WHY. A discount is a price reduction: the
   * guest received the dish, paid less for it, and the VAT base falls with the
   * price. A comp is not a price at all — the guest received it and there is no
   * consideration, so the food leaves stock, the revenue is zero, and it belongs
   * in a comp expense account rather than in a smaller sale.
   *
   * Storing a comp as a 100 % discount is the common shortcut and it destroys
   * food-cost and loss-prevention analytics PERMANENTLY: every comped dish
   * becomes a heavily discounted sale, so cost of sales looks fine and the
   * margin looks terrible, and no later migration can separate them again
   * because the fact was never recorded.
   *
   * `checkTotals` has always handled both correctly and no caller could produce
   * one: `CheckView.totals.discountMinor` and `compMinor` were on every check
   * view in the product, structurally incapable of being anything but zero.
   *
   * ATTRIBUTED, ALWAYS. `authorizedBy` is the signed-in member of staff. An
   * allowance is somebody deciding to give money away; one with no name against
   * it is indistinguishable from a leak, which is the whole reason M8's
   * detection reads the mutation log rather than the cash count.
   *
   * NOTE, NOT DONE: D9 also asks for `approvedBy` above a threshold — a
   * manager's PIN for anything large. The event carries no such field, so it is
   * an event change plus a prompt rather than something to bolt on here, and
   * pretending otherwise by reusing `authorizedBy` would record a server's own
   * approval of their own discount.
   */
  /**
   * Make this check out to a company. §29 ZDPH.
   *
   * ABOVE THE §30a LIMIT THIS IS NOT OPTIONAL. A simplified document issued
   * over 10 000 CZK — which two people having dinner with wine can cross — is
   * not a valid tax document: the guest's employer cannot deduct the VAT, and
   * the venue hears about it weeks later. Until now the till's only behaviour
   * over the threshold was a banner saying so.
   *
   * IT IS ALSO ALLOWED BELOW IT, because a business guest on a 400 Kč lunch
   * still wants their company on the document, and refusing would be the
   * software deciding whose paperwork counts.
   *
   * THE SOURCE IS RECORDED, not inferred. `ARES` means the state asserted these
   * details; `MANUAL` means somebody read them off a card — the only option
   * when the venue's WAN is down, and the case a document is most likely to be
   * wrong in. A till that stamped `ARES` on everything would make the field
   * worthless for exactly the documents worth auditing.
   */
  async identifyBuyer(args: {
    readonly legalName: string;
    readonly registrationId?: string | null;
    readonly vatId?: string | null;
    readonly address?: string | null;
    readonly source: "ARES" | "MANUAL";
  }): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const legalName = args.legalName.trim();
    // The one field a document cannot do without. A blank where the buyer
    // belongs is not a tax document, and the till is the last place that can
    // say so to the person holding the keyboard.
    if (!legalName) return fail("BUYER_NAME_REQUIRED", "pos.error.buyerNameRequired");

    const checkId = this.#checkId;
    // `#guard()` has already refused a session with no order, so this is the
    // narrower case: a view that has not adopted a check identity yet.
    if (!checkId) return fail("NO_ORDER", "pos.error.noOrder");

    /**
     * REFUSED HERE AS WELL AS IN THE REDUCER, for the reason the allowance path
     * gives: the aggregate has to refuse a settled check because the server
     * folds the same events, and catching it here turns an event the device
     * would carry in its outbox forever into a sentence on the screen of the
     * person who asked.
     */
    if (this.#state!.checks.get(checkId)?.closedAt) {
      return fail("CHECK_SETTLED", "pos.error.checkSettled");
    }

    await this.#emit("check.buyer_identified", {
      checkId,
      legalName,
      ...(args.registrationId ? { registrationId: args.registrationId } : {}),
      ...(args.vatId ? { vatId: args.vatId } : {}),
      ...(args.address ? { address: args.address } : {}),
      source: args.source,
    });

    return { ok: true, value: this.view() };
  }

  async applyAllowance(
    selectionId: string,
    args: {
      readonly kind: AllowanceKind;
      readonly amountMinor: bigint;
      readonly reasonCode: string;
      /** The manager who approved it, when that was somebody else. Defaults to whoever is signed in. */
      readonly authorizedBy?: string;
    },
  ): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const selection = this.#state!.selections.get(selectionId);
    if (!selection) return fail("UNKNOWN_LINE", "pos.error.unknownLine", { selectionId });

    /**
     * A VOIDED LINE CANNOT BE DISCOUNTED. It was never sold; taking money off
     * it would put a reduction against nothing and make the day's discount
     * total larger than the day's sales.
     */
    if (selection.voided) return fail("UNKNOWN_LINE", "pos.error.unknownLine", { selectionId });

    if (!args.reasonCode.trim()) return fail("REASON_REQUIRED", "pos.error.reasonRequired");

    /**
     * REFUSED HERE AS WELL AS IN THE REDUCER, and the duplication is deliberate.
     *
     * The aggregate rejects an allowance larger than its line — it has to,
     * because the server folds the same events. Catching it here turns a
     * rejected event that a device would carry in its outbox into a message on
     * the screen of the person who typed the number.
     */
    if (args.amountMinor <= 0n || args.amountMinor > selection.priced.grossMinor) {
      return fail("ALLOWANCE_INVALID", "pos.error.allowanceInvalid", {
        max: selection.priced.grossMinor.toString(),
      });
    }

    await this.#emit("allowance.applied", {
      allowanceId: this.#deps.newId(),
      checkId: this.#checkId!,
      kind: args.kind,
      selectionId,
      amountMinor: args.amountMinor,
      reasonCode: args.reasonCode,
      authorizedBy: args.authorizedBy ?? this.#deps.actorId,
    });

    return { ok: true, value: this.view() };
  }

  // --- Splitting a check ----------------------------------------------------

  /**
   * SPLITTING IS ALLOCATION, NEVER MUTATION — D8, and every operation below is
   * one write of `selection.reallocated` over an immutable selection set.
   *
   * That is what makes the three things that break other tills work here:
   * un-splitting is free, "split then add an item" behaves, and a line is never
   * copied, moved or re-created — so the kitchen ticket, the fired-at anchor
   * and the allergen snapshot survive a split untouched.
   *
   * IN CZECHIA THIS IS CLOSER TO A COMPLIANCE FEATURE THAN A CONVENIENCE ONE.
   * dTest's legal advisor and attorney Ondřej Preuss both hold that a venue
   * cannot require guests to pay together: each guest forms their own contract
   * with the restaurant when they order, and a "pay together" rule is an
   * unenforceable, disproportionate consumer term under the Civil Code even
   * when it is printed on the menu. "Our POS cannot do that" is not a defence.
   *
   * Each resulting check is a SEPARATE TAX DOCUMENT — its own gapless number,
   * its own EET submission — which falls out of the model rather than needing
   * anything special: `closeCheck` already numbers and closes one check.
   */

  /** Every check on this order, as a server sees them. */
  checks(): readonly CheckSummary[] {
    if (!this.#state) return [];
    return [...this.#state.checks.values()].map((check) => ({
      checkId: check.checkId,
      dueMinor: checkTotals(this.#state!, check.checkId).dueMinor,
      outstandingMinor: settlementBalance(this.#state!, check.checkId),
      closed: check.closedAt !== null,
      paid: this.#hasTender(check.checkId),
      current: check.checkId === this.#checkId,
      vacant: check.closedAt === null && isCheckVacant(this.#state!, check.checkId),
    }));
  }

  /**
   * Work on a different check of the same order.
   *
   * A server tenders one guest at a time, so the session keeps a CURRENT check
   * and the screen shows a strip of the others. Returning the view means the
   * caller re-renders without asking twice.
   */
  selectCheck(checkId: string): PosResult<CheckView> {
    if (!this.#state?.checks.has(checkId)) {
      return fail("UNKNOWN_CHECK", "pos.error.unknownCheck", { checkId });
    }
    this.#checkId = checkId;
    return { ok: true, value: this.view() };
  }

  #hasTender(checkId: string): boolean {
    for (const tender of this.#state!.tenders.values()) {
      if (tender.checkId === checkId && !tender.voided) return true;
    }
    return false;
  }

  /**
   * A check that must not gain or lose lines.
   *
   * THE FAILURE THIS PREVENTS IS THE BEST-DOCUMENTED ONE IN THE FIELD: items
   * moved onto a check that is already paid or closed. Toast ships a guard for
   * it that a venue has to telephone support to switch on. Here it is the
   * default and there is no way to turn it off, because the alternative is a
   * guest who has paid and left being charged for somebody else's steak.
   *
   * Per CHECK rather than per order, deliberately. Four guests split, the first
   * pays and goes; the other three may still rearrange between themselves, and
   * refusing that would make the feature useless exactly when it is used.
   */
  #frozen(checkId: string): boolean {
    const check = this.#state!.checks.get(checkId);
    return check === undefined || check.closedAt !== null || this.#hasTender(checkId);
  }

  async #reallocate(
    selectionId: string,
    allocations: readonly { checkId: string; weightBp: number }[],
  ): Promise<PosResult<CheckView>> {
    const selection = this.#state!.selections.get(selectionId);
    if (!selection) return fail("UNKNOWN_LINE", "pos.error.unknownLine", { selectionId });
    if (selection.voided) return fail("UNKNOWN_LINE", "pos.error.unknownLine", { selectionId });

    /**
     * EVERY CHECK WHOSE SHARE MOVES, on both sides of the change.
     *
     * Checking only the destination would let a line be pulled OFF a check
     * somebody has already paid, which leaves them overpaid and the order
     * unbalanced — the same bug in the other direction, and the one nobody
     * writes a support article about.
     */
    const touched = new Set<string>([
      ...selection.allocations.map((a) => a.checkId),
      ...allocations.map((a) => a.checkId),
    ]);
    for (const checkId of touched) {
      if (this.#frozen(checkId)) {
        return fail("CHECK_FROZEN", "pos.error.checkFrozen", { checkId });
      }
    }

    await this.#emit("selection.reallocated", { selectionId, allocations: [...allocations] });
    return { ok: true, value: this.view() };
  }

  /**
   * Split the whole check N ways, the way a table of four asks for it.
   *
   * THE MOST COMMON REQUEST AND THE ONE THE FIELD SAYS TO OPTIMISE FOR: the
   * operational advice everywhere is to pre-split at greeting, and the reason
   * is that splitting a large check afterwards is slow on every till there is.
   * So this is one action, not a per-line exercise — and because it is
   * allocation, doing it afterwards costs the same as doing it first.
   *
   * SEAT NUMBERS ARE NOT REQUIRED. Splitting by seat is the other common
   * design and it fails in practice for a documented reason: it only works if
   * every line is assigned to a seat at order entry, every time, and venues do
   * not sustain that. A feature that depends on a habit nobody keeps is a
   * feature that does not work on a Friday.
   */
  async splitEvenly(ways: number): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    if (!Number.isInteger(ways) || ways < 2 || ways > MAX_SPLIT_WAYS) {
      return fail("SPLIT_REFUSED", "pos.error.splitWays", { max: String(MAX_SPLIT_WAYS) });
    }

    const state = this.#state!;
    const existing = [...state.checks.values()];
    if (existing.some((c) => this.#frozen(c.checkId))) {
      return fail("CHECK_FROZEN", "pos.error.checkFrozen", { checkId: "" });
    }
    // VACANT checks do not count: they are what un-splitting leaves behind,
    // and the screen no longer shows them. Counting them refused a split the
    // screen was offering ("already split") on every table merged back once.
    const live = existing.filter((c) => !isCheckVacant(state, c.checkId));
    if (live.length > 1) {
      // Splitting an already-split order is ambiguous — split which check, into
      // what? Un-split first, which is free, and the screen says so.
      return fail("SPLIT_REFUSED", "pos.error.alreadySplit");
    }

    const base = live[0]?.checkId ?? state.primaryCheckId;
    // Reuse the vacant ones before opening more, so a table split and merged
    // all evening does not grow a pile of empty checks in its log.
    const spare = existing.map((c) => c.checkId).filter((id) => id !== base);
    const checkIds = [base];
    for (let i = 1; i < ways; i++) {
      const reused = spare.shift();
      if (reused !== undefined) {
        checkIds.push(reused);
        continue;
      }
      const checkId = this.#deps.newId();
      await this.#emit("check.opened", { checkId });
      checkIds.push(checkId);
    }

    let index = 0;
    for (const selection of this.#state!.selections.values()) {
      if (selection.voided) continue;
      // The rotation is what stops the first guest carrying every leftover.
      const result = await this.#reallocate(
        selection.selectionId,
        evenWeights(checkIds, index++),
      );
      if (!result.ok) return result;
    }

    return { ok: true, value: this.view() };
  }

  /**
   * Put one line entirely on one check — splitting by item.
   *
   * The other half of how a real table divides a bill: "I had the steak." It
   * composes with `splitEvenly` because both are allocations, so a table can
   * split four ways and then move the wine onto one check without any of it
   * being a special case.
   */
  async moveSelection(selectionId: string, toCheckId: string): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;
    if (!this.#state!.checks.has(toCheckId)) {
      return fail("UNKNOWN_CHECK", "pos.error.unknownCheck", { checkId: toCheckId });
    }
    return this.#reallocate(selectionId, [{ checkId: toCheckId, weightBp: 10_000 }]);
  }

  /**
   * One line across several checks — the shared bottle of wine.
   *
   * Every till worth having does this and it is the case a naive "move the line"
   * model cannot express at all. Because the split is a weight rather than a
   * copy, three people sharing a 189 CZK dish are three allocations of one
   * selection: the kitchen still made one dish, the stock still moved once, and
   * the money divides exactly.
   */
  async splitSelection(
    selectionId: string,
    acrossCheckIds: readonly string[],
  ): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const unique = [...new Set(acrossCheckIds)];
    if (unique.length < 2) {
      return fail("SPLIT_REFUSED", "pos.error.splitNeedsTwo");
    }
    const unknown = unique.find((id) => !this.#state!.checks.has(id));
    if (unknown !== undefined) {
      return fail("UNKNOWN_CHECK", "pos.error.unknownCheck", { checkId: unknown });
    }
    return this.#reallocate(selectionId, evenWeights(unique));
  }

  /**
   * Put it all back on one check.
   *
   * D8 PROMISES THIS IS FREE and it is the reason the model was chosen: a
   * server who split four ways by mistake, or a table that changes its mind
   * after the plates are cleared, costs one action rather than a manager and a
   * void. On a till that splits by creating new orders, this is where the
   * money goes missing.
   *
   * Checks that have been paid or closed are left alone — their guests have
   * gone — and everything still open collapses onto the primary check.
   */
  async unsplit(): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const target = this.#state!.primaryCheckId;
    if (this.#frozen(target)) {
      return fail("CHECK_FROZEN", "pos.error.checkFrozen", { checkId: target });
    }

    for (const selection of this.#state!.selections.values()) {
      if (selection.voided) continue;
      const alreadyThere =
        selection.allocations.length === 1 && selection.allocations[0]!.checkId === target;
      if (alreadyThere) continue;

      // A line sitting on a check somebody has already settled stays there.
      if (selection.allocations.some((a) => this.#frozen(a.checkId))) continue;

      const result = await this.#reallocate(selection.selectionId, [
        { checkId: target, weightBp: 10_000 },
      ]);
      if (!result.ok) return result;
    }

    this.#checkId = target;
    return { ok: true, value: this.view() };
  }

  async voidLine(selectionId: string, args: VoidArgs): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    if (!this.#state!.selections.has(selectionId)) {
      return fail("UNKNOWN_LINE", "pos.error.unknownLine", { selectionId });
    }
    // Checked BEFORE the event exists. A void with no reason must never reach
    // the log to be argued about later — the reason code is what makes void
    // abuse detectable at all.
    if (!args.reasonCode.trim()) return fail("REASON_REQUIRED", "pos.error.reasonRequired");

    await this.#emit("selection.voided", {
      selectionId,
      reason: args.reasonCode,
      authorizedBy: args.authorizedBy,
    });
    return { ok: true, value: this.view() };
  }

  /**
   * Record a payment.
   *
   * Returns the PLAN as well as the view, because the change owed is the one
   * number the person at the till needs immediately and it is deliberately not
   * a tender: a 500 note against a 235,00 bill records 235,00 and hands back
   * 265,00. See ./tender.ts.
   */
  async tender(args: TenderArgs): Promise<PosResult<{ view: CheckView; plan: TenderPlan }>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    // Refused BEFORE the event exists. A tender that cannot balance must never
    // reach the log, where it would sit as a check nobody can ever close.
    const attempt = tryPlanTender({
      outstandingMinor: settlementBalance(this.#state!, this.#checkId!),
      kind: args.kind,
      ...(args.offeredMinor !== undefined ? { offeredMinor: args.offeredMinor } : {}),
      ...(args.tipMinor !== undefined ? { tipMinor: args.tipMinor } : {}),
      ...(args.cashIncrementMinor !== undefined
        ? { cashIncrementMinor: args.cashIncrementMinor }
        : {}),
    });

    if (!attempt.ok) {
      // The CODE becomes the message key. The Error's own text is a log line
      // and must never reach a screen a guest can read over the till.
      return fail("TENDER_REFUSED", `pos.error.tender.${attempt.code}`);
    }
    const plan = attempt.plan;

    await this.#emit("tender.recorded", {
      tenderId: this.#deps.newId(),
      checkId: this.#checkId!,
      kind: plan.kind,
      amountMinor: plan.amountMinor,
      tipMinor: plan.tipMinor,
      roundingMinor: plan.roundingMinor,
    });
    return { ok: true, value: { view: this.view(), plan } };
  }

  /**
   * A COUNTER SALE: paid, then fired, then closed — in that order, because a
   * settled order takes no new sends. A payment that leaves a balance is
   * recorded (it is money taken) and nothing is fired: the kitchen does not
   * make a coffee for a sale that has not happened. If the close then finds no
   * receipt number, the food is already on its way and the sale waits for one,
   * as every close does.
   */
  async payAndFire(args: TenderArgs): Promise<PosResult<{ view: CheckView; plan: TenderPlan }>> {
    const paid = await this.tender(args);
    if (!paid.ok) return paid;
    const outstanding = settlementBalance(this.#state!, this.#checkId!);
    if (outstanding !== 0n) {
      return fail("NOT_BALANCED", "pos.error.notBalanced", { outstandingMinor: outstanding.toString() });
    }
    const fired = await this.fire();
    if (!fired.ok) return fired;
    const closed = await this.closeCheck();
    if (!closed.ok) return closed;
    return { ok: true, value: { view: this.view(), plan: paid.value.plan } };
  }

  /**
   * Reverse a tender rung by mistake.
   *
   * The guest says cash, the till rounds 234,50 up to 235,00, and then they
   * produce a card. THE ROUNDING GOES WITH THE TENDER — it is a function of
   * the tender, not of the check — and the aggregate takes both out together
   * so the reversal cannot be got half right.
   */
  async voidTender(tenderId: string, args: TenderVoidArgs): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    if (!this.#state!.tenders.has(tenderId)) {
      return fail("UNKNOWN_TENDER", "pos.error.unknownTender", { tenderId });
    }
    if (!args.reasonCode.trim()) return fail("REASON_REQUIRED", "pos.error.reasonRequired");

    await this.#emit("tender.voided", {
      tenderId,
      reasonCode: args.reasonCode,
      authorizedBy: args.authorizedBy,
    });
    return { ok: true, value: this.view() };
  }

  async closeCheck(): Promise<PosResult<CheckView>> {
    const blocked = this.#guard();
    if (blocked) return blocked;

    const outstanding = settlementBalance(this.#state!, this.#checkId!);
    if (outstanding !== 0n) {
      // The amount travels with the failure so the screen can say "8 900
      // outstanding" rather than "cannot close".
      return fail("NOT_BALANCED", "pos.error.notBalanced", {
        outstandingMinor: outstanding.toString(),
      });
    }

    /**
     * THE NUMBER IS ISSUED BEFORE ANYTHING IS EMITTED, AND ITS ABSENCE STOPS
     * THE CLOSE.
     *
     * A receipt with no number is not a receipt — it cannot be reported and it
     * cannot be found again. So a till with no numbers refuses to CLOSE, and
     * emits NEITHER event: a `check.closed` without its `receipt.issued` is a
     * settled sale that can never be reported, which is worse than a check that
     * is still open and visibly stuck.
     *
     * Everything else on the till keeps working. Opening a table, ringing an
     * item and firing to the kitchen are all untouched: a seated guest must
     * still be served and the kitchen must still get the food.
     *
     * `issue()` has already advanced and PERSISTED the cursor by the time it
     * returns, so if the emit below fails the number is burnt — a gap, which is
     * detected, attributed and reported. The other order would reissue it, and
     * a duplicate fiscal document is not repairable.
     */
    const issued = (await this.#deps.receiptNumbers?.issue(this.#checkId!)) ?? null;
    if (!issued) {
      return fail("NO_RECEIPT_NUMBER", "pos.error.noReceiptNumber");
    }

    /**
     * BEFORE `check.closed`, and the order is asserted end to end.
     *
     * The fiscal projection folds this stream and reads the number when it sees
     * the close. Arriving after it would make a perfectly good sale
     * unreportable for no reason other than emission order.
     */
    await this.#emit("receipt.issued", {
      checkId: this.#checkId!,
      leaseId: issued.leaseId,
      seriesKey: issued.seriesKey,
      period: issued.period,
      number: issued.number,
      receiptNumber: issued.receiptNumber,
    });

    await this.#emit("check.closed", { checkId: this.#checkId! });

    // The check has settled, so a staged menu may safely take effect.
    if (this.#pendingMenu && this.#state?.status === "SETTLED") {
      this.#menu = this.#pendingMenu;
      this.#pendingMenu = null;
    }
    return { ok: true, value: this.view() };
  }

  /** Pure read. Changes nothing and emits nothing. */
  /**
   * STORNO: take a closed check back. Not an edit — a corrective document from
   * the same gapless series, the money returned the way it was paid, kind by
   * kind. The aggregate refuses anything else, so this builds exactly that.
   */
  async refund(args: { readonly reasonCode: string; readonly authorizedBy: string }): Promise<PosResult<CheckView>> {
    const state = this.#state;
    if (!state || !this.#checkId) return fail("NO_ORDER", "pos.error.noOrder");
    const check = state.checks.get(this.#checkId);
    if (!check?.closedAt) return fail("CHECK_NOT_CLOSED", "pos.refund.notClosed");
    if (check.refund) return fail("ALREADY_REFUNDED", "pos.refund.already");
    if (!args.reasonCode.trim()) return fail("REASON_REQUIRED", "pos.error.reasonRequired");

    const paid = new Map<TenderKind, bigint>();
    for (const t of state.tenders.values()) {
      if (t.checkId !== this.#checkId || t.voided) continue;
      paid.set(t.kind, (paid.get(t.kind) ?? 0n) + t.amountMinor);
    }

    const issued = (await this.#deps.receiptNumbers?.issue(`refund:${this.#checkId}`)) ?? null;
    if (!issued) return fail("NO_RECEIPT_NUMBER", "pos.error.noReceiptNumber");

    await this.#emit("check.refunded", {
      refundId: this.#deps.newId(),
      checkId: this.#checkId,
      reasonCode: args.reasonCode,
      authorizedBy: args.authorizedBy,
      tenders: [...paid].map(([kind, amountMinor]) => ({ kind, amountMinor })),
      leaseId: issued.leaseId,
      seriesKey: issued.seriesKey,
      period: issued.period,
      number: issued.number,
      receiptNumber: issued.receiptNumber,
    });
    return { ok: true, value: this.view() };
  }

  view(): CheckView {
    const state = this.#state;
    if (!state || !this.#checkId) {
      return {
        orderId: null,
        checkId: null,
        tableRef: null,
        consumptionMode: "DINE_IN",
        state: "EMPTY",
        lines: [],
        totals: EMPTY_TOTALS,
        menuVersion: this.#menu.version,
        unfiredCount: 0,
        version: 0,
        tenders: [],
        buyer: null,
      };
    }

    const totals = checkTotals(state, this.#checkId);
    const byRef = new Map(this.#menu.items.map((i) => [i.itemRef, i]));

    const lines: LineView[] = [...state.selections.values()].map((selection) => {
      const sent = selection.fulfillmentState !== "NEW" && selection.fulfillmentState !== "HELD";
      const voided = selection.voided !== null;
      const mine = selection.allocations.find((a) => a.checkId === this.#checkId);
      /**
       * THE SHARE, COMPUTED THE SAME WAY THE TOTAL IS.
       *
       * Deliberately `checkTotals`-consistent rather than a second arithmetic:
       * a line that displayed one number while the total used another is how a
       * guest is handed a receipt whose lines do not add up to its own sum.
       */
      const shareBp = mine?.weightBp ?? 0;
      const shareMinor =
        selection.allocations.length <= 1
          ? (mine ? selection.priced.grossMinor : 0n)
          : (selection.priced.grossMinor * BigInt(shareBp)) / 10_000n;
      return {
        selectionId: selection.selectionId,
        itemRef: selection.itemRef,
        name: selection.name,
        quantityMilli: selection.quantityMilli,
        grossMinor: selection.priced.grossMinor,
        shareMinor,
        shareBp,
        rateBp: selection.priced.rateBp,
        stationId: byRef.get(selection.itemRef)?.stationId ?? "expo",
        fulfillmentState: selection.fulfillmentState,
        voided,
        // A sent line is immutable in quantity and price. Voiding it is the
        // supported way to remove it, and that stays available.
        canEditQuantity:
          !sent &&
          !voided &&
          ![...state.allowances.values()].some((a) => a.selectionId === selection.selectionId),
        canVoid: !voided,
        lockedReasonKey: voided ? "pos.line.locked.voided" : sent ? "pos.line.locked.sent" : null,
        modifiers: selection.modifiers,
        note: selection.note,
        courseId: selection.courseId ?? null,
      };
    });

    const opened = this.#events.find((e) => e.type === "order.opened");
    return {
      orderId: state.orderId,
      checkId: this.#checkId,
      tableRef: state.tableRef,
      consumptionMode: state.consumptionMode,
      state: state.status,
      closed: (state.checks.get(this.#checkId)?.closedAt ?? null) !== null,
      // A Date whatever came in: a journal restored from disk carries strings.
      openedAt: opened ? new Date(opened.occurredAt) : null,
      openedBy: opened?.actorId ?? null,
      lines,
      totals: {
        grossMinor: totals.grossMinor,
        discountMinor: totals.discountMinor,
        compMinor: totals.compMinor,
        dueMinor: totals.dueMinor,
        vatMinor: totals.vatMinor,
        roundingMinor: totals.roundingMinor,
        tipMinor: totals.tipMinor,
        outstandingMinor: settlementBalance(state, this.#checkId),
        taxLines: totals.taxLines,
      },
      menuVersion: this.#menu.version,
      unfiredCount: lines.filter((l) => !l.voided && l.fulfillmentState === "NEW").length,
      heldCourses: [
        ...new Set(
          lines
            .filter((l) => !l.voided && l.fulfillmentState === "NEW" && l.courseId !== null && l.courseId !== FIRST_COURSE)
            .map((l) => l.courseId!),
        ),
      ].sort(),
      version: state.version,
      // Voided ones stay listed. A tender rung and reversed is evidence, and
      // the person at the till needs to see what they already took.
      tenders: [...state.tenders.values()]
        .filter((t) => t.checkId === this.#checkId)
        .map((t) => ({
          tenderId: t.tenderId,
          kind: t.kind,
          amountMinor: t.amountMinor,
          roundingMinor: t.roundingMinor,
          tipMinor: t.tipMinor,
          voided: t.voided !== null,
        })),
      // THIS check's buyer, never the order's. A split table is one order and
      // several documents.
      buyer: state.checks.get(this.#checkId)?.buyer ?? null,
      refund: state.checks.get(this.#checkId)?.refund ?? null,
    };
  }
}

